import React from 'react'
import 'react-contexify/dist/ReactContexify.css'
import { styled } from '@mui/material/styles'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import AcUnitIcon from '@mui/icons-material/AcUnit'
import AddBoxIcon from '@mui/icons-material/AddBox'
import BlurOnIcon from '@mui/icons-material/BlurOn'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import HubIcon from '@mui/icons-material/Hub'
import IndeterminateCheckBox from '@mui/icons-material/IndeterminateCheckBox'
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'

import BusinessIcon from '@mui/icons-material/Business'
import DatasetLinkedIcon from '@mui/icons-material/DatasetLinked'
import DownloadIcon from '@mui/icons-material/Download'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'

import { Box, CircularProgress } from '@mui/material'
import { AppStorage } from 'src/utils'
import { HoverBarScope } from '../tc_scopes'

/** Explica no tooltip por que o download está indisponível, em vez de só desabilitar o botão. */
function tituloDownload(rotulo: string, baixando: boolean, temDados: boolean) {
    if (baixando) {
        return `Baixando ${rotulo}...`
    }

    if (!temDados) {
        return `Faça uma busca para habilitar o download de ${rotulo}`
    }

    return `Baixar ${rotulo}`
}

const LOG = Logger.get('TC-HOVERBAR')

const appStorage = AppStorage.singleton()

const Bar = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    padding: 5,
    borderRadius: 'var(--radius)',
    border: '1px solid var(--border)',
    backgroundColor: 'rgba(255, 255, 255, .94)',
    boxShadow: 'var(--shadow-1)'
})

const BarButton = styled(IconButton)({
    position: 'relative',
    width: 34,
    height: 34,
    borderRadius: 8,
    color: '#2d332f',
    '&:hover': { backgroundColor: '#edf1ee', color: 'var(--green-800)' },
    '&.Mui-disabled': { color: '#b9bfbb' },
    '& .MuiSvgIcon-root': { fontSize: 20 }
})

const DownloadBadge = styled(Box)({
    position: 'absolute',
    right: 1,
    bottom: 1,
    display: 'grid',
    placeItems: 'center',
    '& .MuiSvgIcon-root': { fontSize: 13 }
})

const BadgeProgress = styled(CircularProgress)({
    color: 'var(--green-700)'
})

export type HoverBarViewProps = {
    className?: string
    scope: HoverBarScope
}

export const HoverBarView = function ({ className, scope }: HoverBarViewProps) {
    bindUpdate(React, scope)

    const handleOnLabelOnOffClicked = React.useCallback(() => scope.onToggleLabels().catch(LOG.caught), [scope])
    const handleOnSizeOnOffClicked = React.useCallback(() => scope.onToggleGraphSize().catch(LOG.caught), [scope])
    const handleOnZoomOutClicked = React.useCallback(() => scope.onZoomOut().catch(LOG.caught), [scope])
    const handleOnZoomInClicked = React.useCallback(() => scope.onZoomIn().catch(LOG.caught), [scope])
    const handleDownloadEmpresasClicked = React.useCallback(
        () => scope.onDownloadXlsxEmpresas().catch(LOG.caught),
        [scope]
    )
    const handleDownloadSociosClicked = React.useCallback(() => scope.onDownloadXlsxSocios().catch(LOG.caught), [scope])
    const handleDownloadMedicosClicked = React.useCallback(
        () => scope.onDownloadXlsxMedicos().catch(LOG.caught),
        [scope]
    )
    const handleDownloadVinculosClicked = React.useCallback(
        () => scope.onDownloadXlsxVinculos().catch(LOG.caught),
        [scope]
    )
    const handleToggleGraphExpanded = React.useCallback(() => scope.onToggleGraphExpanded().catch(LOG.caught), [scope])

    const renderDownloadBadge = (downloading: boolean) => (
        <DownloadBadge>{!downloading ? <DownloadIcon /> : <BadgeProgress size={12} />}</DownloadBadge>
    )

    return (
        <Bar className={className}>
            {scope.downloadEnabled && (
                <>
                    {appStorage.tcCompaniesEnabled && (
                        <Tooltip
                            title={tituloDownload(
                                'empresas em planilha',
                                scope.isEmpresaDownloading,
                                scope.isDisplayingData
                            )}
                            placement="left"
                        >
                            <span>
                                <BarButton
                                    aria-label="Baixar empresas em planilha"
                                    disabled={scope.isEmpresaDownloading || !scope.isDisplayingData}
                                    onClick={handleDownloadEmpresasClicked}
                                >
                                    <BusinessIcon />
                                    {renderDownloadBadge(scope.isEmpresaDownloading)}
                                </BarButton>
                            </span>
                        </Tooltip>
                    )}
                    {appStorage.tcPartnersEnabled && (
                        <Tooltip
                            title={tituloDownload(
                                'sócios em planilha',
                                scope.isSociosDownloading,
                                scope.isDisplayingData
                            )}
                            placement="left"
                        >
                            <span>
                                <BarButton
                                    aria-label="Baixar sócios em planilha"
                                    disabled={scope.isSociosDownloading || !scope.isDisplayingData}
                                    onClick={handleDownloadSociosClicked}
                                >
                                    <PeopleAltIcon />
                                    {renderDownloadBadge(scope.isSociosDownloading)}
                                </BarButton>
                            </span>
                        </Tooltip>
                    )}
                    {appStorage.tcDoctorsEnabled && (
                        <Tooltip
                            title={tituloDownload(
                                'médicos em planilha',
                                scope.isMedicosDownloading,
                                scope.isDisplayingData
                            )}
                            placement="left"
                        >
                            <span>
                                <BarButton
                                    aria-label="Baixar médicos em planilha"
                                    disabled={scope.isMedicosDownloading || !scope.isDisplayingData}
                                    onClick={handleDownloadMedicosClicked}
                                >
                                    <MedicalServicesIcon />
                                    {renderDownloadBadge(scope.isMedicosDownloading)}
                                </BarButton>
                            </span>
                        </Tooltip>
                    )}
                    {appStorage.tcRelationshipsEnabled && (
                        <Tooltip
                            title={tituloDownload(
                                'vínculos em planilha',
                                scope.isVinculosDownloading,
                                scope.isDisplayingData
                            )}
                            placement="left"
                        >
                            <span>
                                <BarButton
                                    aria-label="Baixar vínculos em planilha"
                                    disabled={scope.isVinculosDownloading || !scope.isDisplayingData}
                                    onClick={handleDownloadVinculosClicked}
                                >
                                    <DatasetLinkedIcon />
                                    {renderDownloadBadge(scope.isVinculosDownloading)}
                                </BarButton>
                            </span>
                        </Tooltip>
                    )}
                </>
            )}

            <Tooltip title={scope.labelEnabled ? 'Ocultar textos dos nós' : 'Mostrar textos dos nós'} placement="left">
                <BarButton
                    aria-label={scope.labelEnabled ? 'Ocultar textos dos nós' : 'Mostrar textos dos nós'}
                    onClick={handleOnLabelOnOffClicked}
                >
                    {scope.labelEnabled && <FormatListBulletedIcon />}
                    {!scope.labelEnabled && <BlurOnIcon />}
                </BarButton>
            </Tooltip>
            <Tooltip title={scope.allNodesEnabled ? 'Todos os nós possíveis' : 'Nós reduzidos'} placement="left">
                <BarButton
                    aria-label={scope.allNodesEnabled ? 'Todos os nós possíveis' : 'Nós reduzidos'}
                    onClick={handleOnSizeOnOffClicked}
                >
                    {scope.allNodesEnabled && <HubIcon />}
                    {!scope.allNodesEnabled && <AcUnitIcon />}
                </BarButton>
            </Tooltip>
            <Tooltip title="Aproximar" placement="left">
                <BarButton aria-label="Aproximar" onClick={handleOnZoomInClicked}>
                    <AddBoxIcon />
                </BarButton>
            </Tooltip>
            <Tooltip title="Afastar" placement="left">
                <BarButton aria-label="Afastar" onClick={handleOnZoomOutClicked}>
                    <IndeterminateCheckBox />
                </BarButton>
            </Tooltip>
            {scope.expandEnabled && (
                <Tooltip title={scope.graphExpanded ? 'Restaurar grafo' : 'Maximizar grafo'} placement="left">
                    <BarButton
                        aria-label={scope.graphExpanded ? 'Restaurar grafo' : 'Maximizar grafo'}
                        onClick={handleToggleGraphExpanded}
                    >
                        {scope.graphExpanded && <CloseFullscreenRoundedIcon />}
                        {!scope.graphExpanded && <LaunchRoundedIcon />}
                    </BarButton>
                </Tooltip>
            )}
        </Bar>
    )
}

export default HoverBarView
